<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';

/* global axios */

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const inboxId = computed(() => props.inbox.id);
const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);
const { t } = useI18n();
const pluginConfig = computed(
  () => window.chatwoot?.plugins?.['quepasa-inbox']?.config || {}
);
const syncStatusEndpoint = computed(
  () =>
    `/api/v1/accounts/${accountId.value}/quepasa/${inboxId.value}/sync/status`
);
const autoVerifyConfig = computed(
  () => pluginConfig.value.SYNC_AUTO_VERIFY || {}
);
const autoVerifyEnabled = computed(() => autoVerifyConfig.value.ENABLED);
const autoVerifyIntervalMs = computed(() =>
  Number(autoVerifyConfig.value.INTERVAL_MS)
);
const autoVerifyMaxCycles = computed(() =>
  Number(autoVerifyConfig.value.MAX_CYCLES)
);
const syncAllLimit = ref(1000);
const syncBatchSize = ref(100);

// Direct import manual controls (max scan window, batch size, optional chat filter)
const directImportLimit = ref(1000);
const directImportBatchSize = ref(100);
const directImportChatId = ref('');

// Expansion panel states
const automationExpanded = ref(false);
const messageSyncExpanded = ref(false);
const contactSyncExpanded = ref(false);
const directImportExpanded = ref(false);

// Per-inbox auto-sync settings (method + interval + enabled)
const autoSyncSettingsEndpoint = computed(
  () =>
    `/api/v1/accounts/${accountId.value}/quepasa/${inboxId.value}/sync/settings`
);
const autoSyncLoading = ref(false);
const autoSyncSaving = ref(false);
const autoSyncEnabled = ref(true);
const autoSyncMethod = ref('n8n');
const autoSyncIntervalValue = ref(5);
const autoSyncIntervalUnit = ref('m');
const autoSyncLastRunAt = ref(null);
const autoSyncMethodOptions = computed(() => [
  { value: 'n8n', label: t('QUEPASA_INBOX.SYNC.AUTOMATION.METHODS.N8N') },
  { value: 'direct', label: t('QUEPASA_INBOX.SYNC.AUTOMATION.METHODS.DIRECT') },
]);
const autoSyncUnitOptions = computed(() => [
  { value: 's', label: t('QUEPASA_INBOX.SYNC.AUTOMATION.UNITS.SECOND') },
  { value: 'm', label: t('QUEPASA_INBOX.SYNC.AUTOMATION.UNITS.MINUTE') },
  { value: 'h', label: t('QUEPASA_INBOX.SYNC.AUTOMATION.UNITS.HOUR') },
  { value: 'd', label: t('QUEPASA_INBOX.SYNC.AUTOMATION.UNITS.DAY') },
  { value: 'mo', label: t('QUEPASA_INBOX.SYNC.AUTOMATION.UNITS.MONTH') },
]);

const loadAutoSyncSettings = async () => {
  autoSyncLoading.value = true;
  try {
    const { data } = await axios.get(autoSyncSettingsEndpoint.value);
    const settings = data.settings || {};
    autoSyncEnabled.value = settings.enabled !== false;
    autoSyncMethod.value = settings.method || 'n8n';
    autoSyncIntervalValue.value = settings.interval_value || 5;
    autoSyncIntervalUnit.value = settings.interval_unit || 'm';
    autoSyncLastRunAt.value = settings.last_run_at || null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      '[PLUGIN][QUEPASA-INBOX] Failed to load auto-sync settings',
      error
    );
    useAlert(t('QUEPASA_INBOX.SYNC.AUTOMATION.LOAD_ERROR'));
  } finally {
    autoSyncLoading.value = false;
  }
};

const saveAutoSyncSettings = async () => {
  autoSyncSaving.value = true;
  try {
    const { data } = await axios.put(autoSyncSettingsEndpoint.value, {
      enabled: autoSyncEnabled.value,
      method: autoSyncMethod.value,
      interval_value: autoSyncIntervalValue.value,
      interval_unit: autoSyncIntervalUnit.value,
    });
    const settings = data.settings || {};
    autoSyncLastRunAt.value = settings.last_run_at || null;
    useAlert(t('QUEPASA_INBOX.SYNC.AUTOMATION.SAVE_SUCCESS'));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      '[PLUGIN][QUEPASA-INBOX] Failed to save auto-sync settings',
      error
    );
    useAlert(t('QUEPASA_INBOX.SYNC.AUTOMATION.SAVE_ERROR'));
  } finally {
    autoSyncSaving.value = false;
  }
};

watch(automationExpanded, async expanded => {
  if (expanded) {
    await loadAutoSyncSettings();
  }
});

// Direct import (n8n-free) state
const directImportLoading = ref(false);
const directImportRunning = ref(false);
const directImportPreview = ref(null);
const directImportStatus = ref(null);
let directImportTimerId = null;

// Message sync state
const checking = ref(false);
const syncing = ref(false);
const missingMessages = ref([]);
const pendingMessages = ref([]);
const transportFailedMessages = ref([]);
const lastChecked = ref(null);
const reportUrl = ref(null);
const lastSyncRun = ref(null);
const messageSearch = ref('');
const autoVerifyState = ref({
  active: false,
  cycle: 0,
  exhausted: false,
  lastAutoCheckAt: null,
  nextAutoCheckAt: null,
});
const queueRefreshState = ref({
  active: false,
  nextRefreshAt: null,
});
const countdownNow = ref(Date.now());
const stats = ref({
  total: 0,
  total_in_cache: 0,
  ignored: 0,
  synced: 0,
  pending: 0,
  failed: 0,
  transport_failed: 0,
});

// Contact sync state
const fetchingContacts = ref(false);
const contactsData = ref(null);
const contactsFetchedAt = ref(null);
const importing = ref(false);
const tagsList = ref(['quepasa', 'imported']);
const currentTag = ref('');
const tagInput = ref(null);
let autoVerifyTimerId = null;
let queueRefreshTimerId = null;
let countdownTimerId = null;

const clearAutoVerifyTimer = () => {
  if (autoVerifyTimerId) {
    window.clearTimeout(autoVerifyTimerId);
    autoVerifyTimerId = null;
  }
};

const clearQueueRefreshTimer = () => {
  if (queueRefreshTimerId) {
    window.clearTimeout(queueRefreshTimerId);
    queueRefreshTimerId = null;
  }
};

const clearCountdownTimer = () => {
  if (countdownTimerId) {
    window.clearInterval(countdownTimerId);
    countdownTimerId = null;
  }
};

const startCountdownTimer = () => {
  if (
    countdownTimerId ||
    (!autoVerifyState.value.nextAutoCheckAt &&
      !queueRefreshState.value.nextRefreshAt)
  ) {
    return;
  }

  countdownTimerId = window.setInterval(() => {
    countdownNow.value = Date.now();
    if (
      (!autoVerifyState.value.active ||
        !autoVerifyState.value.nextAutoCheckAt ||
        autoVerifyState.value.nextAutoCheckAt <= countdownNow.value) &&
      (!queueRefreshState.value.active ||
        !queueRefreshState.value.nextRefreshAt ||
        queueRefreshState.value.nextRefreshAt <= countdownNow.value)
    ) {
      clearCountdownTimer();
    }
  }, 1000);
};

const resetAutoVerifyState = () => {
  clearAutoVerifyTimer();
  clearQueueRefreshTimer();
  clearCountdownTimer();
  autoVerifyState.value = {
    active: false,
    cycle: 0,
    exhausted: false,
    lastAutoCheckAt: null,
    nextAutoCheckAt: null,
  };
  queueRefreshState.value = {
    active: false,
    nextRefreshAt: null,
  };
};

const syncAutoVerifyMeta = () => {
  if (!lastSyncRun.value) return;

  lastSyncRun.value.autoVerifyActive = autoVerifyState.value.active;
  lastSyncRun.value.autoVerifyCycle = autoVerifyState.value.cycle;
  lastSyncRun.value.autoVerifyExhausted = autoVerifyState.value.exhausted;
  lastSyncRun.value.lastAutoCheckAt = autoVerifyState.value.lastAutoCheckAt;
  lastSyncRun.value.nextAutoCheckAt = autoVerifyState.value.nextAutoCheckAt;
};

const stopAutoVerification = ({ exhausted = false } = {}) => {
  clearAutoVerifyTimer();
  autoVerifyState.value = {
    ...autoVerifyState.value,
    active: false,
    exhausted,
    nextAutoCheckAt: null,
  };
  syncAutoVerifyMeta();
  if (!queueRefreshState.value.active) {
    clearCountdownTimer();
  }
};

const stopQueueRefresh = () => {
  clearQueueRefreshTimer();
  queueRefreshState.value = {
    active: false,
    nextRefreshAt: null,
  };
  if (!autoVerifyState.value.active) {
    clearCountdownTimer();
  }
};

const resetSyncReviewState = () => {
  resetAutoVerifyState();
  pendingMessages.value = [];
  transportFailedMessages.value = [];
  reportUrl.value = null;
  lastSyncRun.value = null;
};

const clearDirectImportTimer = () => {
  if (directImportTimerId) {
    window.clearTimeout(directImportTimerId);
    directImportTimerId = null;
  }
};

onBeforeUnmount(() => {
  clearAutoVerifyTimer();
  clearCountdownTimer();
  clearDirectImportTimer();
});

const directImportBase = () =>
  `/api/v1/accounts/${accountId.value}/quepasa/${inboxId.value}/sync`;

const directImportParams = () => {
  const params = {};
  const limit = Number(directImportLimit.value);
  if (limit > 0) params.limit = limit;
  if (directImportChatId.value?.trim())
    params.chatid = directImportChatId.value.trim();
  return params;
};

const fetchDirectImportPreview = async ({ silent = false } = {}) => {
  if (!silent) directImportLoading.value = true;
  try {
    const response = await axios.get(`${directImportBase()}/import_preview`, {
      params: directImportParams(),
    });
    directImportPreview.value = response.data;
    return response.data;
  } catch (error) {
    console.error('[QUEPASA-IMPORT] Error previewing import:', error);
    if (!silent) {
      useAlert(t('QUEPASA_INBOX.SYNC.DIRECT_IMPORT.ALERTS.PREVIEW_ERROR'));
    }
    return null;
  } finally {
    if (!silent) directImportLoading.value = false;
  }
};

const pollDirectImportStatus = async executionId => {
  try {
    const response = await axios.get(`${directImportBase()}/import_status`, {
      params: executionId ? { execution_id: executionId } : {},
    });
    directImportStatus.value = response.data;

    if (response.data?.active) {
      directImportTimerId = window.setTimeout(
        () => pollDirectImportStatus(executionId),
        2000
      );
    } else {
      directImportRunning.value = false;
      clearDirectImportTimer();
      await fetchDirectImportPreview({ silent: true });
      useAlert(
        t('QUEPASA_INBOX.SYNC.DIRECT_IMPORT.ALERTS.FINISHED', {
          imported: response.data?.imported || 0,
          placeholders: response.data?.media_placeholder || 0,
          errors: response.data?.errors || 0,
        })
      );
    }
  } catch (error) {
    console.error('[QUEPASA-IMPORT] Error polling import status:', error);
    directImportRunning.value = false;
    clearDirectImportTimer();
  }
};

const startDirectImport = async () => {
  directImportRunning.value = true;
  try {
    const batchSize = Number(directImportBatchSize.value);
    const response = await axios.post(`${directImportBase()}/import_direct`, {
      ...directImportParams(),
      ...(batchSize > 0 ? { batch_size: batchSize } : {}),
    });
    const executionId = response.data?.execution_id;
    directImportStatus.value = response.data;
    clearDirectImportTimer();
    directImportTimerId = window.setTimeout(
      () => pollDirectImportStatus(executionId),
      1500
    );
  } catch (error) {
    console.error('[QUEPASA-IMPORT] Error starting direct import:', error);
    directImportRunning.value = false;
    useAlert(t('QUEPASA_INBOX.SYNC.DIRECT_IMPORT.ALERTS.START_ERROR'));
  }
};

watch(directImportExpanded, async expanded => {
  if (expanded) {
    await fetchDirectImportPreview();
  }
});

const checkServerConfigured = async () => {
  try {
    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/quepasa/server/info?inbox_id=${inboxId.value}`
    );

    // Server is configured if it has a wid (WhatsApp ID) inside server object
    return response.data && response.data.server && response.data.server.wid;
  } catch (error) {
    console.error('[QUEPASA-SYNC] Error checking server status:', error);
    return false;
  }
};

const fetchContacts = async () => {
  fetchingContacts.value = true;
  try {
    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/quepasa/${inboxId.value}/contacts/fetch`
    );

    if (response.data.success) {
      contactsData.value = response.data;
      contactsFetchedAt.value = response.data.fetched_at;
      useAlert(t('QUEPASA_INBOX.SYNC.CONTACT.ALERTS.FETCH_SUCCESS'));
    } else {
      useAlert(t('QUEPASA_INBOX.SYNC.CONTACT.ALERTS.FETCH_ERROR'));
    }
  } catch (error) {
    console.error('[QUEPASA-CONTACTS] Error fetching contacts:', error);
    useAlert(t('QUEPASA_INBOX.SYNC.CONTACT.ALERTS.FETCH_ERROR'));
  } finally {
    fetchingContacts.value = false;
  }
};

function addCurrentTag() {
  const tag = currentTag.value.trim().replace(/,$/, ''); // Remove trailing comma
  if (tag && !tagsList.value.includes(tag.toLowerCase())) {
    tagsList.value.push(tag.toLowerCase());
  }
  currentTag.value = '';
}

const handleTagInput = event => {
  const key = event.key;

  // Add tag on Enter, Space, or Comma
  if (key === 'Enter' || key === ' ' || key === ',') {
    event.preventDefault();
    addCurrentTag();
  }

  // Remove last tag on Backspace if input is empty
  if (key === 'Backspace' && !currentTag.value && tagsList.value.length > 0) {
    tagsList.value.pop();
  }
};

const handlePaste = event => {
  event.preventDefault();

  // Get pasted text
  const pastedText = event.clipboardData.getData('text');

  // Split by comma, space, or newline
  const tags = pastedText
    .split(/[,\s\n]+/)
    .map(tag => tag.trim())
    .filter(tag => tag.length > 0)
    .map(tag => tag.toLowerCase());

  // Add each tag if not already in list
  tags.forEach(tag => {
    if (!tagsList.value.includes(tag)) {
      tagsList.value.push(tag);
    }
  });

  // Clear input
  currentTag.value = '';
};

const removeTag = index => {
  tagsList.value.splice(index, 1);
};

const importContacts = async () => {
  importing.value = true;
  try {
    const response = await axios.post(
      `/api/v1/accounts/${accountId.value}/quepasa/${inboxId.value}/contacts/import`,
      { tags: tagsList.value }
    );

    if (response.data.success) {
      const {
        created,
        updated,
        skipped_existing,
        skipped_no_data,
        error_count,
      } = response.data;

      const tagsMsg =
        tagsList.value.length > 0
          ? ` ${t('QUEPASA_INBOX.SYNC.CONTACT.ALERTS.WITH_TAGS', {
              tags: tagsList.value.join(', '),
            })}`
          : '';
      useAlert(
        t('QUEPASA_INBOX.SYNC.CONTACT.ALERTS.IMPORT_SUCCESS', {
          tags: tagsMsg,
          created,
          updated,
          skipped: skipped_existing + skipped_no_data,
          errors: error_count,
        })
      );

      // Refresh contact stats
      await fetchContacts();
    } else {
      useAlert(t('QUEPASA_INBOX.SYNC.CONTACT.ALERTS.IMPORT_ERROR'));
    }
  } catch (error) {
    console.error('[QUEPASA-CONTACTS] Error importing contacts:', error);
    useAlert(t('QUEPASA_INBOX.SYNC.CONTACT.ALERTS.IMPORT_ERROR'));
  } finally {
    importing.value = false;
  }
};

const checkMissingMessages = async ({
  preserveSyncReview = false,
  silent = false,
} = {}) => {
  if (!silent) {
    checking.value = true;
  }
  try {
    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/quepasa/${inboxId.value}/sync/missing_messages`
    );

    if (!preserveSyncReview) {
      resetSyncReviewState();
    }

    missingMessages.value = response.data.missing_messages;
    stats.value = preserveSyncReview
      ? {
          ...stats.value,
          total: response.data.total,
          total_in_cache: response.data.total_in_cache,
          ignored: response.data.ignored || 0,
        }
      : {
          total: response.data.total,
          total_in_cache: response.data.total_in_cache,
          ignored: response.data.ignored || 0,
          synced: 0,
          pending: 0,
          failed: 0,
          transport_failed: 0,
        };
    lastChecked.value = response.data.checked_at;

    if (!preserveSyncReview) {
      useAlert(t('QUEPASA_INBOX.SYNC.MESSAGE.ALERTS.CHECK_SUCCESS'));
    }

    return response.data;
  } catch (error) {
    console.error('[QUEPASA-SYNC] Error checking messages:', error);
    if (!silent) {
      useAlert(t('QUEPASA_INBOX.SYNC.MESSAGE.ALERTS.CHECK_ERROR'));
    }
    return null;
  } finally {
    if (!silent) {
      checking.value = false;
    }
  }
};

const applyBackgroundVerificationStatus = status => {
  if (!status?.execution_id) {
    return null;
  }

  reportUrl.value = status.report_url || reportUrl.value;
  pendingMessages.value = status.pending_messages || [];
  transportFailedMessages.value = status.transport_failed_messages || [];
  lastSyncRun.value = {
    executionId: status.execution_id,
    rounds: status.rounds || lastSyncRun.value?.rounds || [],
    batchesProcessed:
      status.batches_processed || lastSyncRun.value?.batchesProcessed || 0,
    totalRedispatched:
      status.total_redispatched || lastSyncRun.value?.totalRedispatched || 0,
    autoVerifyActive: !!status.active,
    autoVerifyCycle: status.cycle || 0,
    autoVerifyExhausted: !!status.exhausted,
    autoVerifiedCount: status.auto_verified_count || 0,
    lastAutoCheckAt: status.last_checked_at || null,
    nextAutoCheckAt: lastSyncRun.value?.nextAutoCheckAt || null,
  };

  autoVerifyState.value = {
    active: !!status.active,
    cycle: status.cycle || 0,
    exhausted: !!status.exhausted,
    lastAutoCheckAt: status.last_checked_at || null,
    nextAutoCheckAt: autoVerifyState.value.nextAutoCheckAt || null,
  };

  stats.value = {
    ...stats.value,
    synced: status.synced || 0,
    pending: status.pending || 0,
    failed: status.failed || 0,
    transport_failed: status.transport_failed || 0,
    total: status.failed || 0,
  };

  syncAutoVerifyMeta();
  return status;
};

const fetchBackgroundVerificationStatus = async ({
  executionId = null,
  silent = false,
} = {}) => {
  try {
    const response = await axios.get(syncStatusEndpoint.value, {
      params: executionId ? { execution_id: executionId } : {},
    });

    return applyBackgroundVerificationStatus(response.data);
  } catch (error) {
    console.error('[QUEPASA-SYNC] Error loading sync status:', error);
    if (!silent) {
      useAlert(t('QUEPASA_INBOX.SYNC.MESSAGE.ALERTS.CHECK_ERROR'));
    }
    return null;
  }
};

const scheduleQueueRefresh = () => {
  clearQueueRefreshTimer();

  if (!messageSyncExpanded.value) {
    stopQueueRefresh();
    return;
  }

  queueRefreshState.value = {
    active: true,
    nextRefreshAt: Date.now() + autoVerifyIntervalMs.value,
  };
  countdownNow.value = Date.now();
  startCountdownTimer();

  queueRefreshTimerId = window.setTimeout(async () => {
    if (!messageSyncExpanded.value) {
      stopQueueRefresh();
      return;
    }

    if (syncing.value || checking.value) {
      scheduleQueueRefresh();
      return;
    }

    if (lastSyncRun.value?.executionId) {
      await fetchBackgroundVerificationStatus({
        executionId: lastSyncRun.value.executionId,
        silent: true,
      });
    }

    await checkMissingMessages({ preserveSyncReview: true, silent: true });
    scheduleQueueRefresh();
  }, autoVerifyIntervalMs.value);
};

const scheduleAutoVerification = () => {
  clearAutoVerifyTimer();

  if (
    !autoVerifyEnabled.value ||
    !lastSyncRun.value?.executionId ||
    !pendingMessages.value.length
  ) {
    stopAutoVerification({
      exhausted: lastSyncRun.value?.autoVerifyExhausted || false,
    });
    return;
  }

  autoVerifyState.value = {
    ...autoVerifyState.value,
    active: true,
    exhausted: false,
    nextAutoCheckAt: Date.now() + autoVerifyIntervalMs.value,
  };
  countdownNow.value = Date.now();
  syncAutoVerifyMeta();
  startCountdownTimer();

  autoVerifyTimerId = window.setTimeout(async () => {
    if (syncing.value || checking.value) {
      scheduleAutoVerification();
      return;
    }

    const status = await fetchBackgroundVerificationStatus({
      executionId: lastSyncRun.value?.executionId,
      silent: true,
    });

    if (!status?.execution_id) {
      stopAutoVerification();
      return;
    }

    if (!status.active) {
      stopAutoVerification({ exhausted: !!status.exhausted });
      await checkMissingMessages({ preserveSyncReview: true, silent: true });
      return;
    }

    scheduleAutoVerification();
  }, autoVerifyIntervalMs.value);
};

// Watch for contact panel expansion
watch(contactSyncExpanded, async expanded => {
  // Always fetch when expanding, but check server status first
  if (expanded) {
    // Check if server is initialized and configured
    const serverConfigured = await checkServerConfigured();
    if (serverConfigured) {
      fetchContacts();
    } else {
      // Reset expansion if server not configured
      contactSyncExpanded.value = false;
      useAlert(t('QUEPASA_INBOX.SYNC.CONTACT.ALERTS.SERVER_NOT_CONFIGURED'));
    }
  }
});

watch(messageSyncExpanded, async expanded => {
  if (!expanded) {
    stopQueueRefresh();
    return;
  }

  await checkMissingMessages({ preserveSyncReview: true, silent: true });

  const backgroundStatus = await fetchBackgroundVerificationStatus({
    silent: true,
  });
  if (backgroundStatus?.active) {
    scheduleAutoVerification();
  }

  scheduleQueueRefresh();
});

const syncAllMessages = async () => {
  if (!stats.value || stats.value.total === 0) return;

  syncing.value = true;
  try {
    const response = await axios.post(
      `/api/v1/accounts/${accountId.value}/quepasa/${inboxId.value}/sync/sync_all`,
      {
        limit: Number(syncAllLimit.value) || 1000,
        batch_size: Number(syncBatchSize.value) || 100,
      }
    );

    const {
      execution_id,
      synced,
      failed,
      pending = failed,
      transport_failed = 0,
      rounds = [],
      batches_processed = 0,
      total_redispatched = 0,
      pending_messages = [],
      transport_failed_messages = [],
      background_verification,
      report_url,
    } = response.data;

    // Store report URL
    reportUrl.value = report_url;

    const backgroundStatus = applyBackgroundVerificationStatus(
      background_verification
    );

    if (!backgroundStatus) {
      pendingMessages.value = pending_messages;
      transportFailedMessages.value = transport_failed_messages;
      lastSyncRun.value = {
        executionId: execution_id,
        rounds,
        batchesProcessed: batches_processed,
        totalRedispatched: total_redispatched,
        autoVerifyActive: false,
        autoVerifyCycle: 0,
        autoVerifyExhausted: false,
        autoVerifiedCount: 0,
        lastAutoCheckAt: null,
        nextAutoCheckAt: null,
      };
      resetAutoVerifyState();
    }

    // Update stats
    if (stats.value) {
      stats.value.synced = synced;
      stats.value.pending = pending;
      stats.value.failed = failed;
      stats.value.transport_failed = transport_failed;
      stats.value.total = failed;
    }

    useAlert(
      t('QUEPASA_INBOX.SYNC.MESSAGE.ALERTS.SYNC_FINISHED', {
        synced,
        pending,
        transport_failed,
      })
    );

    await checkMissingMessages({ preserveSyncReview: true });

    // Refresh the list
    if (failed === 0) {
      missingMessages.value = [];
    } else if (backgroundStatus?.active) {
      scheduleAutoVerification();
    }
  } catch (error) {
    console.error('[QUEPASA-SYNC] Error syncing messages:', error);
    useAlert(t('QUEPASA_INBOX.SYNC.MESSAGE.ALERTS.SYNC_ERROR'));
  } finally {
    syncing.value = false;
  }
};

const downloadReport = () => {
  if (!reportUrl.value) return;

  // Open report in new tab
  window.open(reportUrl.value, '_blank');
};

const normalizedMessageSearch = computed(() =>
  messageSearch.value.trim().toLowerCase()
);

const nextAutoVerifyCountdownSeconds = computed(() => {
  if (!autoVerifyState.value.active || !autoVerifyState.value.nextAutoCheckAt) {
    return null;
  }

  return Math.max(
    0,
    Math.ceil(
      (autoVerifyState.value.nextAutoCheckAt - countdownNow.value) / 1000
    )
  );
});

const formattedNextAutoVerifyCountdown = computed(() => {
  if (nextAutoVerifyCountdownSeconds.value === null) return null;

  const minutes = Math.floor(nextAutoVerifyCountdownSeconds.value / 60);
  const seconds = nextAutoVerifyCountdownSeconds.value % 60;

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
});

const showNextAutoVerifyCountdown = computed(
  () =>
    !!lastSyncRun.value?.autoVerifyActive &&
    !!formattedNextAutoVerifyCountdown.value
);

const nextQueueRefreshCountdownSeconds = computed(() => {
  if (
    !queueRefreshState.value.active ||
    !queueRefreshState.value.nextRefreshAt
  ) {
    return null;
  }

  return Math.max(
    0,
    Math.ceil(
      (queueRefreshState.value.nextRefreshAt - countdownNow.value) / 1000
    )
  );
});

const formattedNextQueueRefreshCountdown = computed(() => {
  if (nextQueueRefreshCountdownSeconds.value === null) return null;

  const minutes = Math.floor(nextQueueRefreshCountdownSeconds.value / 60);
  const seconds = nextQueueRefreshCountdownSeconds.value % 60;

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
});

const matchesSearch = message => {
  if (!normalizedMessageSearch.value) return true;

  const haystack = [
    message.id,
    message.from,
    message.text,
    message.type,
    message.status,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return haystack.includes(normalizedMessageSearch.value);
};

const filteredMissingMessages = computed(() =>
  missingMessages.value.filter(message => matchesSearch(message))
);

const filteredPendingMessages = computed(() =>
  pendingMessages.value.filter(message => matchesSearch(message))
);

const filteredTransportFailedMessages = computed(() =>
  transportFailedMessages.value.filter(message => matchesSearch(message))
);

const formatDate = dateString => {
  return new Date(dateString).toLocaleString();
};

const formatStatus = status => {
  if (!status) return '-';

  switch (status) {
    case 'synced':
      return t('QUEPASA_INBOX.SYNC.MESSAGE.STATUS.SYNCED');
    case 'pending':
      return t('QUEPASA_INBOX.SYNC.MESSAGE.STATUS.PENDING');
    case 'failed':
      return t('QUEPASA_INBOX.SYNC.MESSAGE.STATUS.FAILED');
    case 'redispatched':
      return t('QUEPASA_INBOX.SYNC.MESSAGE.STATUS.REDISPATCHED');
    default:
      return status;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4 p-6">
    <!-- Automation (per-inbox auto-sync) Expansion Panel -->
    <div class="border border-n-slate-6 rounded-lg overflow-hidden">
      <button
        class="w-full flex items-center justify-between p-4 bg-n-slate-2 hover:bg-n-slate-3 transition-colors"
        @click="automationExpanded = !automationExpanded"
      >
        <div class="flex items-center gap-3">
          <div class="text-xl">⏱️</div>
          <div class="text-left">
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ t('QUEPASA_INBOX.SYNC.AUTOMATION.TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11">
              {{ t('QUEPASA_INBOX.SYNC.AUTOMATION.DESCRIPTION') }}
            </p>
          </div>
        </div>
        <div
          class="text-n-slate-11 transition-transform"
          :class="{ 'rotate-180': automationExpanded }"
        >
          ▼
        </div>
      </button>

      <div v-show="automationExpanded" class="p-6 bg-n-slate-1">
        <div v-if="autoSyncLoading" class="text-sm text-n-slate-11">
          {{ t('QUEPASA_INBOX.SYNC.AUTOMATION.LOADING') }}
        </div>
        <div v-else class="flex flex-col gap-4">
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input v-model="autoSyncEnabled" type="checkbox" />
            <span>{{ t('QUEPASA_INBOX.SYNC.AUTOMATION.ENABLED') }}</span>
          </label>

          <label class="flex flex-col gap-2 text-sm text-n-slate-11">
            <span>{{ t('QUEPASA_INBOX.SYNC.AUTOMATION.METHOD_LABEL') }}</span>
            <select
              v-model="autoSyncMethod"
              :disabled="!autoSyncEnabled"
              class="rounded-md border border-n-slate-6 bg-n-slate-1 p-2 text-n-slate-12"
            >
              <option
                v-for="option in autoSyncMethodOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
            <span class="text-xs text-n-slate-10">
              {{ t('QUEPASA_INBOX.SYNC.AUTOMATION.METHOD_HELP') }}
            </span>
          </label>

          <label class="flex flex-col gap-2 text-sm text-n-slate-11">
            <span>{{ t('QUEPASA_INBOX.SYNC.AUTOMATION.INTERVAL_LABEL') }}</span>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
              <input
                v-model.number="autoSyncIntervalValue"
                type="number"
                min="1"
                :disabled="!autoSyncEnabled"
                class="w-full rounded-md border border-n-slate-6 bg-n-slate-1 p-2 text-n-slate-12"
              />
              <select
                v-model="autoSyncIntervalUnit"
                :disabled="!autoSyncEnabled"
                class="w-full rounded-md border border-n-slate-6 bg-n-slate-1 p-2 text-n-slate-12"
              >
                <option
                  v-for="option in autoSyncUnitOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
            <span class="text-xs text-n-slate-10">
              {{ t('QUEPASA_INBOX.SYNC.AUTOMATION.INTERVAL_HELP') }}
            </span>
          </label>

          <div class="flex items-center justify-end">
            <NextButton
              :is-loading="autoSyncSaving"
              :label="t('QUEPASA_INBOX.SYNC.AUTOMATION.SAVE')"
              @click="saveAutoSyncSettings"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Message Sync Expansion Panel -->
    <div class="border border-n-slate-6 rounded-lg overflow-hidden">
      <button
        class="w-full flex items-center justify-between p-4 bg-n-slate-2 hover:bg-n-slate-3 transition-colors"
        @click="messageSyncExpanded = !messageSyncExpanded"
      >
        <div class="flex items-center gap-3">
          <div class="text-xl">📨</div>
          <div class="text-left">
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11">
              {{ t('QUEPASA_INBOX.SYNC.MESSAGE.DESCRIPTION') }}
            </p>
          </div>
        </div>
        <div
          class="text-n-slate-11 transition-transform"
          :class="{ 'rotate-180': messageSyncExpanded }"
        >
          ▼
        </div>
      </button>

      <div v-show="messageSyncExpanded" class="p-6 bg-n-slate-1">
        <!-- Header Actions -->
        <div
          class="mb-6 grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end"
        >
          <div class="grid gap-4 md:grid-cols-2">
            <label class="flex flex-col gap-2 text-sm text-n-slate-11">
              <span>{{
                t('QUEPASA_INBOX.SYNC.MESSAGE.CONTROLS.SYNC_LIMIT')
              }}</span>
              <input
                v-model.number="syncAllLimit"
                type="number"
                min="1"
                step="1"
                class="w-full px-4 py-2 rounded-lg bg-n-slate-2 border border-n-slate-6 text-n-slate-12"
              />
            </label>
            <label class="flex flex-col gap-2 text-sm text-n-slate-11">
              <span>{{
                t('QUEPASA_INBOX.SYNC.MESSAGE.CONTROLS.BATCH_SIZE')
              }}</span>
              <input
                v-model.number="syncBatchSize"
                type="number"
                min="1"
                step="1"
                class="w-full px-4 py-2 rounded-lg bg-n-slate-2 border border-n-slate-6 text-n-slate-12"
              />
            </label>
          </div>
          <NextButton
            :loading="checking"
            :disabled="checking"
            @click="checkMissingMessages"
          >
            {{ t('QUEPASA_INBOX.SYNC.MESSAGE.ACTIONS.CHECK') }}
          </NextButton>
        </div>
        <p class="mb-6 text-sm text-n-slate-11">
          {{
            t('QUEPASA_INBOX.SYNC.MESSAGE.CONTROLS.HELP', {
              limit: syncAllLimit,
              batchSize: syncBatchSize,
            })
          }}
        </p>

        <!-- Last Checked Info -->
        <div v-if="lastChecked" class="flex items-center gap-2 mb-6">
          <div class="text-sm text-n-slate-11">
            {{
              t('QUEPASA_INBOX.SYNC.MESSAGE.LAST_CHECKED', {
                date: formatDate(lastChecked),
              })
            }}
          </div>
        </div>
        <div
          v-if="formattedNextQueueRefreshCountdown"
          class="mb-6 rounded-lg border border-n-slate-6 bg-n-slate-2 px-4 py-3 text-sm text-n-slate-11"
        >
          {{
            t('QUEPASA_INBOX.SYNC.MESSAGE.AUTO_REFRESH', {
              countdown: formattedNextQueueRefreshCountdown,
            })
          }}
        </div>

        <div
          v-if="lastSyncRun"
          class="mb-6 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900"
        >
          <div class="font-semibold">
            {{ t('QUEPASA_INBOX.SYNC.MESSAGE.RUN.TITLE') }}
          </div>
          <div class="mt-1 break-all">
            {{
              t('QUEPASA_INBOX.SYNC.MESSAGE.RUN.SUMMARY', {
                executionId: lastSyncRun.executionId || '-',
                rounds: lastSyncRun.rounds.length,
                batches: lastSyncRun.batchesProcessed,
                redispatched: lastSyncRun.totalRedispatched,
              })
            }}
          </div>
          <div class="mt-1 text-xs text-blue-800">
            {{ t('QUEPASA_INBOX.SYNC.MESSAGE.RUN.GUIDE') }}
          </div>
          <div
            v-if="lastSyncRun.autoVerifyActive"
            class="mt-2 text-xs text-blue-800"
          >
            {{
              t('QUEPASA_INBOX.SYNC.MESSAGE.RUN.AUTO_ACTIVE', {
                cycle: lastSyncRun.autoVerifyCycle,
                maxCycles: autoVerifyMaxCycles,
                pending: stats.pending || 0,
              })
            }}
          </div>
          <div
            v-if="showNextAutoVerifyCountdown"
            class="mt-1 text-xs text-blue-800"
          >
            {{
              t('QUEPASA_INBOX.SYNC.MESSAGE.RUN.NEXT_AUTO_CHECK', {
                countdown: formattedNextAutoVerifyCountdown,
              })
            }}
          </div>
          <div
            v-else-if="lastSyncRun.autoVerifyExhausted"
            class="mt-2 text-xs text-amber-700"
          >
            {{
              t('QUEPASA_INBOX.SYNC.MESSAGE.RUN.AUTO_EXHAUSTED', {
                maxCycles: autoVerifyMaxCycles,
                pending: stats.pending || 0,
              })
            }}
          </div>
          <div
            v-else-if="
              lastSyncRun.autoVerifyCycle > 0 && (stats.pending || 0) === 0
            "
            class="mt-2 text-xs text-green-700"
          >
            {{
              t('QUEPASA_INBOX.SYNC.MESSAGE.RUN.AUTO_FINISHED', {
                count: lastSyncRun.autoVerifiedCount || 0,
              })
            }}
          </div>
        </div>

        <div class="mb-6">
          <input
            v-model="messageSearch"
            type="text"
            class="w-full px-4 py-2 rounded-lg bg-n-slate-2 border border-n-slate-6 text-n-slate-12"
            :placeholder="t('QUEPASA_INBOX.SYNC.MESSAGE.FILTER_PLACEHOLDER')"
          />
        </div>

        <!-- Stats -->
        <div v-if="stats" class="grid grid-cols-2 xl:grid-cols-6 gap-4 mb-6">
          <div class="p-4 rounded-lg bg-n-slate-2 border border-n-slate-6">
            <div class="text-sm text-n-slate-11">
              {{ t('QUEPASA_INBOX.SYNC.MESSAGE.STATS.TOTAL_IN_CACHE') }}
            </div>
            <div class="text-2xl font-bold text-n-slate-12 mt-1">
              {{ stats.total_in_cache || 0 }}
            </div>
          </div>
          <div class="p-4 rounded-lg bg-n-slate-2 border border-n-slate-6">
            <div class="text-sm text-n-slate-11">
              {{ t('QUEPASA_INBOX.SYNC.MESSAGE.STATS.MISSING_MESSAGES') }}
            </div>
            <div class="text-2xl font-bold text-n-slate-12 mt-1">
              {{ stats.total || 0 }}
            </div>
          </div>
          <div class="p-4 rounded-lg bg-n-slate-2 border border-n-slate-6">
            <div class="text-sm text-n-slate-11">
              {{ t('QUEPASA_INBOX.SYNC.MESSAGE.STATS.IGNORED') }}
            </div>
            <div class="text-2xl font-bold text-n-slate-12 mt-1">
              {{ stats.ignored || 0 }}
            </div>
          </div>
          <div
            class="p-4 rounded-lg bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700"
          >
            <div class="text-sm text-green-700 dark:text-green-300">
              {{ t('QUEPASA_INBOX.SYNC.MESSAGE.STATS.SYNCED') }}
            </div>
            <div
              class="text-2xl font-bold text-green-800 dark:text-green-100 mt-1"
            >
              {{ stats.synced || 0 }}
            </div>
          </div>
          <div
            class="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700"
          >
            <div class="text-sm text-yellow-700 dark:text-yellow-300">
              {{ t('QUEPASA_INBOX.SYNC.MESSAGE.STATS.PENDING_VERIFICATION') }}
            </div>
            <div
              class="text-2xl font-bold text-yellow-800 dark:text-yellow-100 mt-1"
            >
              {{ stats.pending || 0 }}
            </div>
          </div>
          <div
            class="p-4 rounded-lg bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700"
          >
            <div class="text-sm text-red-700 dark:text-red-300">
              {{ t('QUEPASA_INBOX.SYNC.MESSAGE.STATS.TRANSPORT_FAILURES') }}
            </div>
            <div class="text-2xl font-bold text-red-800 dark:text-red-100 mt-1">
              {{ stats.transport_failed || 0 }}
            </div>
          </div>
          <div class="p-4 rounded-lg bg-n-slate-2 border border-n-slate-6">
            <div class="text-sm text-n-slate-11">
              {{ t('QUEPASA_INBOX.SYNC.MESSAGE.STATS.TOTAL_REMAINING') }}
            </div>
            <div class="text-2xl font-bold text-n-slate-12 mt-1">
              {{ stats.failed || 0 }}
            </div>
          </div>
        </div>

        <!-- Sync Action -->
        <div v-if="stats.total > 0" class="flex justify-center gap-4">
          <NextButton
            :loading="syncing"
            :disabled="syncing"
            @click="syncAllMessages"
          >
            {{
              t('QUEPASA_INBOX.SYNC.MESSAGE.ACTIONS.SYNC_ALL', {
                count: stats.total,
              })
            }}
          </NextButton>

          <NextButton
            v-if="reportUrl"
            variant="outline"
            @click="downloadReport"
          >
            {{ t('QUEPASA_INBOX.SYNC.MESSAGE.ACTIONS.DOWNLOAD_REPORT') }}
          </NextButton>
        </div>

        <!-- Report Download After Sync -->
        <div
          v-else-if="reportUrl && lastChecked"
          class="flex flex-col items-center justify-center py-12 text-center gap-4"
        >
          <div class="text-4xl mb-4">✅</div>
          <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
            {{ t('QUEPASA_INBOX.SYNC.MESSAGE.STATES.SYNC_COMPLETED') }}
          </h3>
          <p class="text-sm text-n-slate-11 mb-4">
            {{ t('QUEPASA_INBOX.SYNC.MESSAGE.STATES.REPORT_READY') }}
          </p>
          <NextButton @click="downloadReport">
            {{ t('QUEPASA_INBOX.SYNC.MESSAGE.ACTIONS.DOWNLOAD_REPORT') }}
          </NextButton>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="!checking && lastChecked"
          class="flex flex-col items-center justify-center py-12 text-center"
        >
          <div class="text-4xl mb-4">✅</div>
          <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
            {{ t('QUEPASA_INBOX.SYNC.MESSAGE.STATES.NO_MISSING_MESSAGES') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ t('QUEPASA_INBOX.SYNC.MESSAGE.STATES.ALL_MESSAGES_SYNCED') }}
          </p>
        </div>

        <!-- Loading State -->
        <div
          v-else-if="checking"
          class="flex items-center justify-center py-12"
        >
          <div
            class="animate-spin rounded-full h-8 w-8 border-b-2 border-n-brand"
          />
        </div>

        <div v-if="filteredMissingMessages.length > 0" class="mt-8 space-y-4">
          <div class="flex items-center justify-between">
            <div>
              <h4 class="text-base font-semibold text-n-slate-12">
                {{
                  t('QUEPASA_INBOX.SYNC.MESSAGE.SECTIONS.CURRENT_MISSING.TITLE')
                }}
              </h4>
              <p class="text-sm text-n-slate-11">
                {{
                  t(
                    'QUEPASA_INBOX.SYNC.MESSAGE.SECTIONS.CURRENT_MISSING.DESCRIPTION'
                  )
                }}
              </p>
            </div>
            <div class="text-sm text-n-slate-11">
              {{
                t('QUEPASA_INBOX.SYNC.MESSAGE.SHOWN_COUNT', {
                  count: filteredMissingMessages.length,
                })
              }}
            </div>
          </div>

          <div
            class="max-h-96 overflow-auto rounded-lg border border-n-slate-6"
          >
            <table class="w-full text-sm">
              <thead class="bg-n-slate-2 text-n-slate-11 sticky top-0">
                <tr>
                  <th class="text-left px-4 py-3">
                    {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TABLE.TYPE') }}
                  </th>
                  <th class="text-left px-4 py-3">
                    {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TABLE.FROM') }}
                  </th>
                  <th class="text-left px-4 py-3">
                    {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TABLE.TIMESTAMP') }}
                  </th>
                  <th class="text-left px-4 py-3">
                    {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TABLE.PREVIEW') }}
                  </th>
                  <th class="text-left px-4 py-3">
                    {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TABLE.MESSAGE_ID') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="message in filteredMissingMessages"
                  :key="`missing-${message.id}`"
                  class="border-t border-n-slate-6 align-top"
                >
                  <td class="px-4 py-3 text-n-slate-12">
                    {{ message.type || '-' }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-12">
                    {{ message.from || '-' }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-12">
                    {{ formatDate(message.timestamp) }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-12 break-words max-w-md">
                    {{ message.text || '-' }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-11 font-mono break-all">
                    {{ message.id }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="filteredPendingMessages.length > 0" class="mt-8 space-y-4">
          <div class="flex items-center justify-between">
            <div>
              <h4 class="text-base font-semibold text-n-slate-12">
                {{ t('QUEPASA_INBOX.SYNC.MESSAGE.SECTIONS.PENDING.TITLE') }}
              </h4>
              <p class="text-sm text-n-slate-11">
                {{
                  t('QUEPASA_INBOX.SYNC.MESSAGE.SECTIONS.PENDING.DESCRIPTION')
                }}
              </p>
            </div>
            <div class="text-sm text-n-slate-11">
              {{
                t('QUEPASA_INBOX.SYNC.MESSAGE.SHOWN_COUNT', {
                  count: filteredPendingMessages.length,
                })
              }}
            </div>
          </div>

          <div
            class="max-h-96 overflow-auto rounded-lg border border-yellow-200 dark:border-yellow-700"
          >
            <table class="w-full text-sm">
              <thead
                class="bg-yellow-50 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 sticky top-0"
              >
                <tr>
                  <th class="text-left px-4 py-3">
                    {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TABLE.STATUS') }}
                  </th>
                  <th class="text-left px-4 py-3">
                    {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TABLE.ATTEMPTS') }}
                  </th>
                  <th class="text-left px-4 py-3">
                    {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TABLE.TYPE') }}
                  </th>
                  <th class="text-left px-4 py-3">
                    {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TABLE.FROM') }}
                  </th>
                  <th class="text-left px-4 py-3">
                    {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TABLE.TIMESTAMP') }}
                  </th>
                  <th class="text-left px-4 py-3">
                    {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TABLE.PREVIEW') }}
                  </th>
                  <th class="text-left px-4 py-3">
                    {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TABLE.MESSAGE_ID') }}
                  </th>
                  <th class="text-left px-4 py-3">
                    {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TABLE.SEARCH_HINT') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="message in filteredPendingMessages"
                  :key="`pending-${message.id}`"
                  class="border-t border-yellow-100 dark:border-yellow-800 align-top"
                >
                  <td class="px-4 py-3 text-yellow-700 dark:text-yellow-300">
                    {{ formatStatus(message.status) }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-12">
                    {{ message.attempts || 0 }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-12">
                    {{ message.type || '-' }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-12">
                    {{ message.from || '-' }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-12">
                    {{ formatDate(message.timestamp) }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-12 break-words max-w-md">
                    {{ message.text || '-' }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-11 font-mono break-all">
                    {{ message.id }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-11 font-mono break-all">
                    {{ message.lookup_key || '-' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div
          v-if="filteredTransportFailedMessages.length > 0"
          class="mt-8 space-y-4"
        >
          <div class="flex items-center justify-between">
            <div>
              <h4 class="text-base font-semibold text-n-slate-12">
                {{
                  t(
                    'QUEPASA_INBOX.SYNC.MESSAGE.SECTIONS.TRANSPORT_FAILURES.TITLE'
                  )
                }}
              </h4>
              <p class="text-sm text-n-slate-11">
                {{
                  t(
                    'QUEPASA_INBOX.SYNC.MESSAGE.SECTIONS.TRANSPORT_FAILURES.DESCRIPTION'
                  )
                }}
              </p>
            </div>
            <div class="text-sm text-n-slate-11">
              {{
                t('QUEPASA_INBOX.SYNC.MESSAGE.SHOWN_COUNT', {
                  count: filteredTransportFailedMessages.length,
                })
              }}
            </div>
          </div>

          <div
            class="max-h-96 overflow-auto rounded-lg border border-red-200 dark:border-red-700"
          >
            <table class="w-full text-sm">
              <thead
                class="bg-red-50 dark:bg-red-900 text-red-700 dark:text-red-300 sticky top-0"
              >
                <tr>
                  <th class="text-left px-4 py-3">
                    {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TABLE.STATUS') }}
                  </th>
                  <th class="text-left px-4 py-3">
                    {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TABLE.ATTEMPTS') }}
                  </th>
                  <th class="text-left px-4 py-3">
                    {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TABLE.TYPE') }}
                  </th>
                  <th class="text-left px-4 py-3">
                    {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TABLE.FROM') }}
                  </th>
                  <th class="text-left px-4 py-3">
                    {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TABLE.TIMESTAMP') }}
                  </th>
                  <th class="text-left px-4 py-3">
                    {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TABLE.ERROR') }}
                  </th>
                  <th class="text-left px-4 py-3">
                    {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TABLE.MESSAGE_ID') }}
                  </th>
                  <th class="text-left px-4 py-3">
                    {{ t('QUEPASA_INBOX.SYNC.MESSAGE.TABLE.SEARCH_HINT') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="message in filteredTransportFailedMessages"
                  :key="`failed-${message.id}`"
                  class="border-t border-red-100 dark:border-red-800 align-top"
                >
                  <td class="px-4 py-3 text-red-700 dark:text-red-300">
                    {{ formatStatus(message.status) }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-12">
                    {{ message.attempts || 0 }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-12">
                    {{ message.type || '-' }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-12">
                    {{ message.from || '-' }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-12">
                    {{ formatDate(message.timestamp) }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-12 break-words max-w-md">
                    {{ message.error || '-' }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-11 font-mono break-all">
                    {{ message.id }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-11 font-mono break-all">
                    {{ message.lookup_key || '-' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Direct Import (n8n-free) Expansion Panel -->
    <div class="border border-n-slate-6 rounded-lg overflow-hidden">
      <button
        class="w-full flex items-center justify-between p-4 bg-n-slate-2 hover:bg-n-slate-3 transition-colors"
        @click="directImportExpanded = !directImportExpanded"
      >
        <div class="flex items-center gap-3">
          <div class="text-xl">⚡</div>
          <div class="text-left">
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ t('QUEPASA_INBOX.SYNC.DIRECT_IMPORT.TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11">
              {{ t('QUEPASA_INBOX.SYNC.DIRECT_IMPORT.DESCRIPTION') }}
            </p>
          </div>
        </div>
        <div
          class="text-n-slate-11 transition-transform"
          :class="{ 'rotate-180': directImportExpanded }"
        >
          ▼
        </div>
      </button>

      <div v-show="directImportExpanded" class="p-6 bg-n-slate-1">
        <div
          class="mb-6 rounded-lg border border-green-200 bg-green-50 dark:border-green-700 dark:bg-green-900 px-4 py-3 text-sm text-green-900 dark:text-green-100"
        >
          {{ t('QUEPASA_INBOX.SYNC.DIRECT_IMPORT.INFO') }}
        </div>

        <!-- Loading preview -->
        <div
          v-if="directImportLoading"
          class="flex items-center justify-center py-12"
        >
          <div
            class="animate-spin rounded-full h-8 w-8 border-b-2 border-n-brand"
          />
        </div>

        <!-- Preview stats -->
        <div
          v-else-if="directImportPreview"
          class="grid grid-cols-2 xl:grid-cols-5 gap-4 mb-6"
        >
          <div class="p-4 rounded-lg bg-n-slate-2 border border-n-slate-6">
            <div class="text-sm text-n-slate-11">
              {{ t('QUEPASA_INBOX.SYNC.DIRECT_IMPORT.STATS.TOTAL_IN_CACHE') }}
            </div>
            <div class="text-2xl font-bold text-n-slate-12 mt-1">
              {{ directImportPreview.total_in_cache || 0 }}
            </div>
          </div>
          <div
            class="p-4 rounded-lg bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700"
          >
            <div class="text-sm text-blue-700 dark:text-blue-300">
              {{ t('QUEPASA_INBOX.SYNC.DIRECT_IMPORT.STATS.IMPORTABLE') }}
            </div>
            <div
              class="text-2xl font-bold text-blue-800 dark:text-blue-100 mt-1"
            >
              {{ directImportPreview.importable || 0 }}
            </div>
          </div>
          <div class="p-4 rounded-lg bg-n-slate-2 border border-n-slate-6">
            <div class="text-sm text-n-slate-11">
              {{ t('QUEPASA_INBOX.SYNC.DIRECT_IMPORT.STATS.TEXT') }}
            </div>
            <div class="text-2xl font-bold text-n-slate-12 mt-1">
              {{ directImportPreview.text || 0 }}
            </div>
          </div>
          <div class="p-4 rounded-lg bg-n-slate-2 border border-n-slate-6">
            <div class="text-sm text-n-slate-11">
              {{ t('QUEPASA_INBOX.SYNC.DIRECT_IMPORT.STATS.MEDIA') }}
            </div>
            <div class="text-2xl font-bold text-n-slate-12 mt-1">
              {{ directImportPreview.media || 0 }}
            </div>
          </div>
          <div
            class="p-4 rounded-lg bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700"
          >
            <div class="text-sm text-green-700 dark:text-green-300">
              {{ t('QUEPASA_INBOX.SYNC.DIRECT_IMPORT.STATS.ALREADY_PRESENT') }}
            </div>
            <div
              class="text-2xl font-bold text-green-800 dark:text-green-100 mt-1"
            >
              {{ directImportPreview.already_present || 0 }}
            </div>
          </div>
        </div>

        <!-- Progress -->
        <div
          v-if="directImportRunning || directImportStatus?.processed"
          class="mb-6 rounded-lg border border-n-slate-6 bg-n-slate-2 px-4 py-3"
        >
          <div
            class="flex items-center justify-between text-sm text-n-slate-11 mb-2"
          >
            <span>
              {{
                t('QUEPASA_INBOX.SYNC.DIRECT_IMPORT.PROGRESS', {
                  processed: directImportStatus.processed || 0,
                  total: directImportStatus.total || 0,
                })
              }}
            </span>
            <span>
              {{
                t('QUEPASA_INBOX.SYNC.DIRECT_IMPORT.PROGRESS_DETAIL', {
                  imported: directImportStatus.imported || 0,
                  placeholders: directImportStatus.media_placeholder || 0,
                  errors: directImportStatus.errors || 0,
                })
              }}
            </span>
          </div>
          <div class="w-full bg-n-slate-4 rounded-full h-2 overflow-hidden">
            <div
              class="bg-n-brand h-2 rounded-full transition-all"
              :style="{
                width:
                  (directImportStatus.total
                    ? Math.round(
                        (100 * (directImportStatus.processed || 0)) /
                          directImportStatus.total
                      )
                    : 0) + '%',
              }"
            />
          </div>
        </div>

        <!-- Importable messages list (mirrors the n8n missing-messages list) -->
        <div
          v-if="
            directImportPreview &&
            directImportPreview.messages &&
            directImportPreview.messages.length
          "
          class="mb-6 rounded-lg border border-n-slate-6 overflow-hidden"
        >
          <div
            class="px-4 py-2 bg-n-slate-2 text-sm font-semibold text-n-slate-12"
          >
            {{
              t('QUEPASA_INBOX.SYNC.DIRECT_IMPORT.LIST_TITLE', {
                count: directImportPreview.messages.length,
              })
            }}
          </div>
          <div class="max-h-72 overflow-y-auto divide-y divide-n-slate-4">
            <div
              v-for="message in directImportPreview.messages"
              :key="message.id"
              class="px-4 py-2 flex items-start gap-3 text-sm"
            >
              <span
                class="mt-0.5 text-xs px-2 py-0.5 rounded-full"
                :class="
                  message.direction === 'outgoing'
                    ? 'bg-n-brand/10 text-n-brand'
                    : 'bg-n-slate-4 text-n-slate-11'
                "
              >
                {{ message.type || 'text' }}
              </span>
              <div class="min-w-0 flex-1">
                <div class="text-n-slate-12 font-medium truncate">
                  {{ message.from }}
                </div>
                <div class="text-n-slate-11 truncate">
                  {{
                    message.text ||
                    t('QUEPASA_INBOX.SYNC.DIRECT_IMPORT.NO_TEXT')
                  }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Manual controls: max window, batch size, chat filter -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            <span>{{
              t('QUEPASA_INBOX.SYNC.DIRECT_IMPORT.CONTROLS.LIMIT')
            }}</span>
            <input
              v-model.number="directImportLimit"
              type="number"
              min="1"
              :disabled="directImportRunning"
              class="w-full rounded-md border border-n-slate-6 bg-n-slate-1 p-2 text-n-slate-12"
            />
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            <span>{{
              t('QUEPASA_INBOX.SYNC.DIRECT_IMPORT.CONTROLS.BATCH_SIZE')
            }}</span>
            <input
              v-model.number="directImportBatchSize"
              type="number"
              min="1"
              :disabled="directImportRunning"
              class="w-full rounded-md border border-n-slate-6 bg-n-slate-1 p-2 text-n-slate-12"
            />
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            <span>{{
              t('QUEPASA_INBOX.SYNC.DIRECT_IMPORT.CONTROLS.CHAT_FILTER')
            }}</span>
            <input
              v-model="directImportChatId"
              type="text"
              :placeholder="
                t(
                  'QUEPASA_INBOX.SYNC.DIRECT_IMPORT.CONTROLS.CHAT_FILTER_PLACEHOLDER'
                )
              "
              :disabled="directImportRunning"
              class="w-full rounded-md border border-n-slate-6 bg-n-slate-1 p-2 text-n-slate-12"
            />
          </label>
        </div>

        <!-- Action -->
        <div class="flex justify-center gap-4">
          <NextButton
            :loading="directImportRunning"
            :disabled="
              directImportRunning ||
              directImportLoading ||
              !(directImportPreview && directImportPreview.importable > 0)
            "
            @click="startDirectImport"
          >
            {{
              t('QUEPASA_INBOX.SYNC.DIRECT_IMPORT.ACTIONS.IMPORT', {
                count: directImportPreview ? directImportPreview.importable : 0,
              })
            }}
          </NextButton>
          <NextButton
            variant="outline"
            :disabled="directImportLoading || directImportRunning"
            @click="fetchDirectImportPreview"
          >
            {{ t('QUEPASA_INBOX.SYNC.DIRECT_IMPORT.ACTIONS.REFRESH') }}
          </NextButton>
        </div>
      </div>
    </div>

    <!-- Contact Sync Expansion Panel -->
    <div class="border border-n-slate-6 rounded-lg overflow-hidden">
      <button
        class="w-full flex items-center justify-between p-4 bg-n-slate-2 hover:bg-n-slate-3 transition-colors"
        @click="contactSyncExpanded = !contactSyncExpanded"
      >
        <div class="flex items-center gap-3">
          <div class="text-xl">👥</div>
          <div class="text-left">
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ t('QUEPASA_INBOX.SYNC.CONTACT.TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11">
              {{ t('QUEPASA_INBOX.SYNC.CONTACT.DESCRIPTION') }}
            </p>
          </div>
        </div>
        <div
          class="text-n-slate-11 transition-transform"
          :class="{ 'rotate-180': contactSyncExpanded }"
        >
          ▼
        </div>
      </button>

      <div v-show="contactSyncExpanded" class="p-6 bg-n-slate-1">
        <!-- Last Fetched Info -->
        <div v-if="contactsFetchedAt" class="flex items-center gap-2 mb-6">
          <div class="text-sm text-n-slate-11">
            {{
              t('QUEPASA_INBOX.SYNC.CONTACT.LAST_FETCHED', {
                date: formatDate(contactsFetchedAt),
              })
            }}
          </div>
        </div>

        <!-- Contact Stats -->
        <div v-if="contactsData" class="space-y-4 mb-6">
          <!-- First row: 3 columns -->
          <div class="grid grid-cols-3 gap-4">
            <div class="p-4 rounded-lg bg-n-slate-2 border border-n-slate-6">
              <div class="text-sm text-n-slate-11">
                {{ t('QUEPASA_INBOX.SYNC.CONTACT.STATS.TOTAL_IN_QUEPASA') }}
              </div>
              <div class="text-2xl font-bold text-n-slate-12 mt-1">
                {{ contactsData.total || 0 }}
              </div>
            </div>
            <div
              class="p-4 rounded-lg bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700"
            >
              <div class="text-sm text-blue-700 dark:text-blue-300">
                {{ t('QUEPASA_INBOX.SYNC.CONTACT.STATS.NEW_CONTACTS') }}
              </div>
              <div
                class="text-2xl font-bold text-blue-800 dark:text-blue-100 mt-1"
              >
                {{ contactsData.new_contacts || 0 }}
              </div>
            </div>
            <div
              class="p-4 rounded-lg bg-orange-50 dark:bg-orange-900 border border-orange-200 dark:border-orange-700"
            >
              <div class="text-sm text-orange-700 dark:text-orange-300">
                {{ t('QUEPASA_INBOX.SYNC.CONTACT.STATS.NEED_UPDATE') }}
              </div>
              <div
                class="text-2xl font-bold text-orange-800 dark:text-orange-100 mt-1"
              >
                {{ contactsData.update_contacts || 0 }}
              </div>
            </div>
          </div>
          <!-- Second row: 2 columns -->
          <div class="grid grid-cols-2 gap-4">
            <div
              class="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700"
            >
              <div class="text-sm text-yellow-700 dark:text-yellow-300">
                {{ t('QUEPASA_INBOX.SYNC.CONTACT.STATS.IGNORED_NO_DATA') }}
              </div>
              <div
                class="text-2xl font-bold text-yellow-800 dark:text-yellow-100 mt-1"
              >
                {{ contactsData.ignored_contacts || 0 }}
              </div>
            </div>
            <div
              class="p-4 rounded-lg bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700"
            >
              <div class="text-sm text-green-700 dark:text-green-300">
                {{ t('QUEPASA_INBOX.SYNC.CONTACT.STATS.ALREADY_EXISTS') }}
              </div>
              <div
                class="text-2xl font-bold text-green-800 dark:text-green-100 mt-1"
              >
                {{ contactsData.existing_contacts || 0 }}
              </div>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div
          v-if="fetchingContacts"
          class="flex items-center justify-center py-12"
        >
          <div
            class="animate-spin rounded-full h-8 w-8 border-b-2 border-n-brand"
          />
        </div>

        <!-- Tags Input -->
        <div
          v-if="
            contactsData &&
            (contactsData.new_contacts > 0 || contactsData.update_contacts > 0)
          "
          class="mb-6"
        >
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ t('QUEPASA_INBOX.SYNC.CONTACT.TAGS.LABEL') }}
          </label>

          <!-- Tags Display Area (Above) -->
          <div
            v-if="tagsList.length > 0"
            class="flex flex-wrap gap-2 mb-3 p-3 border border-n-slate-6 rounded-lg bg-n-slate-1"
          >
            <span
              v-for="(tag, index) in tagsList"
              :key="index"
              class="inline-flex items-center gap-1 px-2 py-1 bg-n-brand/10 text-n-brand rounded text-sm font-medium"
            >
              <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z"
                  clip-rule="evenodd"
                />
              </svg>
              {{ tag }}
              <button
                type="button"
                class="ml-1 hover:text-red-600 focus:outline-none"
                @click="removeTag(index)"
              >
                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </span>
          </div>

          <!-- Input for new tags (Below, Fixed) -->
          <input
            ref="tagInput"
            v-model="currentTag"
            type="text"
            :placeholder="t('QUEPASA_INBOX.SYNC.CONTACT.TAGS.PLACEHOLDER')"
            class="w-full px-3 py-2 border border-n-slate-6 rounded-lg bg-n-slate-1 text-n-slate-12 placeholder-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent text-sm"
            @keydown="handleTagInput"
            @paste="handlePaste"
            @blur="addCurrentTag"
          />

          <p class="text-xs text-n-slate-11 mt-1">
            {{ t('QUEPASA_INBOX.SYNC.CONTACT.TAGS.HELP') }}
          </p>
        </div>

        <!-- Import Button -->
        <div
          v-if="
            contactsData &&
            (contactsData.new_contacts > 0 || contactsData.update_contacts > 0)
          "
          class="flex justify-center"
        >
          <NextButton
            :loading="importing"
            :disabled="importing"
            @click="importContacts"
          >
            {{
              t('QUEPASA_INBOX.SYNC.CONTACT.ACTIONS.IMPORT_CONTACTS', {
                count:
                  (contactsData.new_contacts || 0) +
                  (contactsData.update_contacts || 0),
              })
            }}
          </NextButton>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="!contactsData && !fetchingContacts"
          class="flex flex-col items-center justify-center py-12 text-center"
        >
          <div class="text-4xl mb-4">👥</div>
          <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
            {{ t('QUEPASA_INBOX.SYNC.CONTACT.STATES.LOADING_CONTACTS') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ t('QUEPASA_INBOX.SYNC.CONTACT.STATES.FETCHING_CONTACTS') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
